import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useSnapshot } from "valtio";
import * as Sentry from "@sentry/react";
import { store, actions } from "@/lib/store";
import { checkSession } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { RouteErrorFallback } from "@/components/ErrorFallback";
import { EventsDashboard } from "@/routes/EventsDashboard";
import { CreateEvent } from "@/routes/CreateEvent";
import { EventDetail } from "@/routes/EventDetail";
import { ReviewUploads } from "@/routes/ReviewUploads";
import { BatchProduce } from "@/routes/BatchProduce";
import { ViewOutputs } from "@/routes/ViewOutputs";
import { Login } from "@/routes/Login";
import { Analytics } from "@/routes/Analytics";

function withRouteErrorBoundary(
  element: React.ReactElement,
  routeName: string
) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <RouteErrorFallback error={error} resetError={resetError} />
      )}
      beforeCapture={(scope) => {
        scope.setTag("boundary", "route");
        scope.setTag("route.name", routeName);
      }}
    >
      {element}
    </Sentry.ErrorBoundary>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAuthLoading } = useSnapshot(store);

  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-organize-bg">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-vizimix-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-vizimix-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function App() {
  useEffect(() => {
    async function initAuth() {
      actions.setAuthLoading(true);
      const { user } = await checkSession();
      actions.setUser(user);
    }
    initAuth();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={withRouteErrorBoundary(
              <EventsDashboard />,
              "events-dashboard"
            )}
          />
          <Route
            path="events/new"
            element={withRouteErrorBoundary(<CreateEvent />, "create-event")}
          />
          <Route
            path="events/:id"
            element={withRouteErrorBoundary(<EventDetail />, "event-detail")}
          />
          <Route
            path="events/:id/review"
            element={withRouteErrorBoundary(
              <ReviewUploads />,
              "review-uploads"
            )}
          />
          <Route
            path="events/:id/produce"
            element={withRouteErrorBoundary(
              <BatchProduce />,
              "batch-produce"
            )}
          />
          <Route
            path="events/:id/outputs"
            element={withRouteErrorBoundary(<ViewOutputs />, "view-outputs")}
          />
          <Route
            path="analytics"
            element={withRouteErrorBoundary(<Analytics />, "analytics")}
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
